import React, { useCallback, useMemo, useState } from 'react';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction, useTranslation } from '@lyearn/i18n';
import { FileTextOutline, Globe2Outline, TagOutline } from '@lyearn/icons';
import { Stack } from '@lyearn/ui';
import { useAppConfig } from '@/hooks/useAppConfig';
import useQueryParam, { StringParam } from '@/hooks/useQueryParam';
import Item from '@/modules/corporate/modules/course/views/CourseBuilder/components/SidebarDetails/Settings/components/Menu/Item';
import { useSurveyEditorContext } from '@/modules/corporate/modules/survey/views/SurveyEditor/context/SurveyEditorContext';
import { SurveyEditorActionEnum } from '@/modules/corporate/modules/survey/views/SurveyEditor/hooks/useSurveyEditor/types';
import { UpdateSurveyInput } from '@/types/schema';

import surveySettingsDialogs from './config/surveySettingsDialogs';
import { DialogState, SurveySettingsDialogTypes } from './types';

const getSettingsItems = ({
  t,
  subWorkspacesEnabled,
  validationError,
  enableAssignmentRestriction,
}: {
  t: TFunction;
  subWorkspacesEnabled: boolean;
  validationError?: string | null;
  enableAssignmentRestriction: boolean;
}) =>
  getEnabledItems([
    {
      key: 'survey-assignment-restriction',
      title: t('Common:Form.AssignmentSettings'),
      icon: <FileTextOutline className="h-20 w-20 me-2" fillColorToken="icon-primary" />,
      dialogComponent: SurveySettingsDialogTypes.AssignmentRestriction,
      enabled: enableAssignmentRestriction,
    },
    {
      key: 'survey-tags',
      title: t('Common:Form.Tags'),
      icon: (
        <TagOutline
          className="h-20 w-20 me-2"
          fillColorToken={validationError ? 'icon-error' : 'icon-primary'}
        />
      ),
      dialogComponent: SurveySettingsDialogTypes.Tags,
      enabled: true,
      error: validationError === 'tag' ? t('Tags:Form.Error.TagsValuesEmpty') : '',
    },
    {
      key: 'workspace-settings',
      title: t('Common:Form.Workspaces'),
      icon: (
        <Globe2Outline
          className="h-20 w-20 me-2"
          fillColorToken={validationError ? 'icon-error' : 'icon-primary'}
        />
      ),
      dialogComponent: SurveySettingsDialogTypes.WorkspaceSettings,
      enabled: subWorkspacesEnabled,
    },
  ]);

function SurveySettings() {
  const { t } = useTranslation();
  const [activeDialog, setActiveDialog] = useState<DialogState | null>(null);
  const { site } = useAppConfig();
  const { subWorkspacesEnabled, enableAssignmentRestriction } = site;
  const [validationError] = useQueryParam('validationError', StringParam);
  const items = useMemo(
    () =>
      getSettingsItems({ t, subWorkspacesEnabled, validationError, enableAssignmentRestriction }),
    [t, subWorkspacesEnabled, validationError, enableAssignmentRestriction],
  );

  const { initialSurvey, onAction } = useSurveyEditorContext();
  const onDialogClose = useCallback(() => {
    setActiveDialog(null);
  }, []);

  const onSubmit = useCallback(
    (updates: Partial<UpdateSurveyInput>) => {
      onAction({
        type: SurveyEditorActionEnum.ExecuteUpdateSurvey,
        payload: { updates },
      });
    },
    [onAction],
  );

  return (
    <Stack className="p-16" direction="col" gapY={4}>
      {items.map(({ key, title, icon, dialogComponent, error }) => (
        <Item
          key={key}
          className="px-8"
          error={error}
          icon={icon}
          title={title}
          onClick={() =>
            setActiveDialog({
              Component: surveySettingsDialogs[dialogComponent],
            })
          }
        />
      ))}
      {activeDialog?.Component && initialSurvey ? (
        <activeDialog.Component
          initialSurvey={initialSurvey}
          onClose={onDialogClose}
          onSubmit={onSubmit}
        />
      ) : null}
    </Stack>
  );
}

export default SurveySettings;
